/**
 * What Amoreg has learned about how the person works in the active
 * organisation (AMO-119): each habit with why it was noted and when it lapses,
 * reworded or deleted in place, or forgotten all at once.
 *
 * Learning itself is switched on and off with "Learn from my conversations"
 * in the Your context form; this section only manages what exists. It stays
 * hidden while the workspace has learning off and nothing has been learned.
 *
 * @module components/settings/LearnedNotesSection
 */
import { LEARNED_NOTE_EXPIRY_DAYS, LEARNED_NOTE_MAX_LENGTH } from "@repo/shared/learnedNotes";
import { useEffect, useState } from "react";

import {
  deleteLearnedNote,
  fetchLearnedNotes,
  forgetLearnedNotes,
  updateLearnedNote,
  type LearnedNote,
  type LearnedNotes,
} from "../../state/learnedNotes";
import { useActiveOrg } from "../../state/orgs";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { SettingsRow, SettingsSection } from "./settingsLayout";

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

export function LearnedNotesSection() {
  const org = useActiveOrg();
  const organizationId = org?.id ?? null;
  const [loaded, setLoaded] = useState<LearnedNotes | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [editing, setEditing] = useState<{ readonly id: string; readonly text: string } | null>(null);
  const [confirmingForget, setConfirmingForget] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!organizationId) return;
    let cancelled = false;
    setLoaded(null);
    setLoadFailed(false);
    setEditing(null);
    setConfirmingForget(false);
    setError(null);
    void fetchLearnedNotes(organizationId).then((result) => {
      if (cancelled) return;
      if (result === null) setLoadFailed(true);
      else setLoaded(result);
    });
    return () => {
      cancelled = true;
    };
  }, [organizationId, loadAttempt]);

  if (!org || !organizationId) return null;
  if (loaded && loaded.mode === "off" && loaded.notes.length === 0) return null;
  if (loaded && !loaded.member) {
    return (
      <SettingsSection id="your-context-learned" title={`What Amoreg has learned in ${org.name}`}>
        <SettingsRow
          id="your-context-learned-not-member"
          title="Not a member here"
          description={`Amoreg learns how you work in organisations you belong to. You can see ${org.name}, but you are not a member, so nothing is learned or kept for you here.`}
        />
      </SettingsSection>
    );
  }
  if (!loaded && !loadFailed) return null;

  const notes = loaded?.notes ?? [];
  const replace = (next: ReadonlyArray<LearnedNote>) =>
    setLoaded((current) => (current ? { ...current, notes: next } : current));

  const run = async (work: () => Promise<string | null>) => {
    setBusy(true);
    setError(null);
    const failure = await work();
    setBusy(false);
    if (failure) setError(failure);
  };

  const save = (id: string, text: string) =>
    run(async () => {
      const result = await updateLearnedNote(organizationId, id, text);
      if (!result.ok) return result.message;
      replace(notes.map((note) => (note.id === id ? result.note : note)));
      setEditing(null);
      return null;
    });

  const remove = (id: string) =>
    run(async () => {
      const failure = await deleteLearnedNote(organizationId, id);
      if (failure) return failure;
      replace(notes.filter((note) => note.id !== id));
      return null;
    });

  const forget = () =>
    run(async () => {
      const failure = await forgetLearnedNotes(organizationId);
      setConfirmingForget(false);
      if (failure) return failure;
      replace([]);
      return null;
    });

  return (
    <SettingsSection
      id="your-context-learned"
      title={`What Amoreg has learned in ${org.name}`}
      headerAction={
        notes.length > 0 ? (
          <div className="flex items-center gap-2">
            {confirmingForget ? (
              <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => setConfirmingForget(false)}>
                Keep them
              </Button>
            ) : null}
            <Button
              type="button"
              size="sm"
              variant={confirmingForget ? "destructive" : "outline"}
              disabled={busy}
              onClick={() => (confirmingForget ? void forget() : setConfirmingForget(true))}
            >
              {confirmingForget ? "Forget all" : "Forget everything"}
            </Button>
          </div>
        ) : null
      }
    >
      {loadFailed ? (
        <p role="alert" className="px-3 py-2 text-sm text-destructive sm:px-4">
          What Amoreg has learned could not be loaded.
          <Button type="button" size="sm" variant="outline" className="ml-2" onClick={() => setLoadAttempt((n) => n + 1)}>
            Try again
          </Button>
        </p>
      ) : notes.length === 0 ? (
        <SettingsRow
          id="your-context-learned-empty"
          title="Nothing learned yet"
          description={`As you work, Amoreg notices the habits you repeat — the format you ask for, the steps you take, the sources you check first — and uses them in new conversations in ${org.name}. Notes appear here, and lapse after ${LEARNED_NOTE_EXPIRY_DAYS} days without use.`}
        />
      ) : (
        notes.map((note) =>
          editing?.id === note.id ? (
            <SettingsRow
              key={note.id}
              id={`learned-${note.id}`}
              title="Edit note"
              description={note.reason}
              control={
                <div className="flex items-center gap-2">
                  <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => setEditing(null)}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    disabled={busy || editing.text.trim().length === 0 || editing.text.trim() === note.text}
                    onClick={() => void save(note.id, editing.text)}
                  >
                    Save note
                  </Button>
                </div>
              }
            >
              <Input
                aria-label="Note"
                value={editing.text}
                maxLength={LEARNED_NOTE_MAX_LENGTH}
                disabled={busy}
                className="mt-2"
                onChange={(event) => setEditing({ id: note.id, text: event.currentTarget.value })}
              />
            </SettingsRow>
          ) : (
            <SettingsRow
              key={note.id}
              id={`learned-${note.id}`}
              title={note.text}
              description={`${note.reason} · Lapses ${shortDate(note.expiresAt)} unless used again`}
              control={
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => setEditing({ id: note.id, text: note.text })}
                  >
                    Edit
                  </Button>
                  <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => void remove(note.id)}>
                    Delete
                  </Button>
                </div>
              }
            />
          ),
        )
      )}
      {error ? (
        <p role="alert" className="px-3 pb-2 text-sm text-destructive sm:px-4">
          {error}
        </p>
      ) : null}
    </SettingsSection>
  );
}
